import { dataURLToBlob } from 'blob-util'
import { DiagnosticRTCCustomDevice } from './types'
import { getFirstAudioTrackLabel, normalizeLabel } from './components/utils'

export function captureVideoScreenshot(videoElement: HTMLVideoElement, width = 700): File {
  let height = 0
  height = videoElement.videoHeight / (videoElement.videoWidth / width)

  // Firefox currently has a bug where the height can't be read from
  // the video, so we will make assumptions if this happens.

  if (isNaN(height)) {
    height = width / (4 / 3)
  }

  const canvas = document.createElement('canvas')

  canvas.width = width
  canvas.height = height

  const context = canvas.getContext('2d')
  context?.drawImage(videoElement, 0, 0, canvas.width, canvas.height)

  const imgDataUrl = canvas.toDataURL('image/png')
  const blob = dataURLToBlob(imgDataUrl)
  const imgName = `medical-screenshot-${Date.now().toString()}.png`
  const file = new File([blob], imgName, { type: 'image/png' })
  return file
}

export const microphonePluginConstraintsGenerator = (audioDeviceId?: string): MediaStreamConstraints => ({
  video: false,
  audio: {
    // Enforce exact device when provided, otherwise allow default selection
    ...(audioDeviceId ? { deviceId: { exact: audioDeviceId } as ConstrainDOMString } : { deviceId: { ideal: 'default' } as ConstrainDOMString }),
    // Disable browser DSP to preserve raw signal
    noiseSuppression: { ideal: false },
    autoGainControl: { ideal: false },
    echoCancellation: { ideal: false },
    channelCount: { ideal: 1 },
    // Relax format hints to avoid constraint failures and browser fallbacks
    sampleSize: { ideal: 16 },
    sampleRate: { ideal: 44100 },
  },
})

export function normalizeDevice(device: DiagnosticRTCCustomDevice | MediaDeviceInfo) {
  if ('device' in device) {
    return {
      deviceId: device.device.deviceId,
      label: device.device.label,
      type: device.type,
    }
  }

  return {
    deviceId: device.deviceId,
    label: device.label,
    type: device.kind,
  }
}

export async function selectDeviceWithRetry(deviceId: string, initialStream: MediaStream, serviceName: string = 'Audio') {
  // Resolve requested device label from the browser's enumerateDevices
  let requestedLabel: string | undefined
  let deviceExistsInBrowser = false
  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    const audioInputs = devices.filter((d) => d.kind === 'audioinput')
    const match = audioInputs.find((d) => d.deviceId === deviceId)
    deviceExistsInBrowser = !!match
    requestedLabel = match?.label
    console.warn(`🌐 ${serviceName.toUpperCase()} BROWSER MEDIA DEVICES:`, {
      requestedDeviceId: deviceId,
      deviceExistsInBrowser,
      totalBrowserAudioInputs: audioInputs.length,
      browserAudioDevices: audioInputs.map((d) => ({ id: d.deviceId, label: d.label })),
    })
  } catch (e) {
    console.error(`${serviceName} enumerateDevices failed:`, e)
  }

  // Compute mismatch using browser label if available
  let workingStream: MediaStream = initialStream
  let actualTrackLabel = getFirstAudioTrackLabel(workingStream)
  const baseMatch = requestedLabel
    ? normalizeLabel(actualTrackLabel).includes(normalizeLabel(requestedLabel)) ||
      normalizeLabel(requestedLabel).includes(normalizeLabel(actualTrackLabel.split('(')[0] || ''))
    : true // if we can't determine, assume match to avoid false negatives

  if (!baseMatch && deviceExistsInBrowser) {
    // Retry once with exact deviceId after permission/context warm-up
    try {
      const retryStream = await navigator.mediaDevices.getUserMedia(microphonePluginConstraintsGenerator(deviceId))
      const retryLabel = getFirstAudioTrackLabel(retryStream)
      const retryMatch = requestedLabel
        ? normalizeLabel(retryLabel) === normalizeLabel(requestedLabel) || normalizeLabel(retryLabel).includes(normalizeLabel(requestedLabel))
        : true
      if (retryMatch) {
        // Replace working stream with the retried one
        workingStream.getTracks().forEach((t) => t.stop())
        workingStream = retryStream
        actualTrackLabel = retryLabel
      }
    } catch (e) {
      console.error(`${serviceName} retry getUserMedia failed:`, e)
    }
  }

  // Final mismatch check after retry
  const finalMismatch = requestedLabel
    ? !normalizeLabel(actualTrackLabel).includes(normalizeLabel(requestedLabel)) &&
      !normalizeLabel(requestedLabel).includes(normalizeLabel(actualTrackLabel))
    : false

  return {
    workingStream,
    requestedLabel,
    actualTrackLabel,
    deviceExistsInBrowser,
    mismatch: finalMismatch,
  }
}
